import { Checkbox } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';

import type { PackagingTypeValue } from './constants';
import { PACKAGING_TYPE_GROUPS } from './constants';
import * as Styled from './styles';

export type Props = {
  value?: PackagingTypeValue[];
  onChange?: (value: PackagingTypeValue[]) => void;
  disabled?: boolean;
  'data-testid'?: string;
};

const PackagingTypeSelect = forwardRef<HTMLDivElement, Props>(
  ({ value = [], onChange, disabled, 'data-testid': testId }, ref) => {
    const [open, setOpen] = useState(false);

    useEffect(() => {
      if (disabled) setOpen(false);
    }, [disabled]);

    const selectedSet = useMemo(() => new Set(value), [value]);

    const handleCheckboxChange = useCallback(
      (optionValue: PackagingTypeValue, e: CheckboxChangeEvent) => {
        if (!onChange) return;
        const next = e.target.checked
          ? [...value, optionValue]
          : value.filter(v => v !== optionValue);
        onChange(next);
      },
      [value, onChange]
    );

    const displayText = useMemo(() => {
      if (!value.length) return undefined;
      if (value.length <= 2) {
        return value
          .map(v => {
            const [type, treatment] = v.split('_');
            return `${type.toUpperCase()}-${treatment === 'treated' ? 'T' : 'U'}`;
          })
          .join(', ');
      }
      return `${value.length} selected`;
    }, [value]);

    const content = (
      <Styled.DropdownContent
        data-testid={testId ? `${testId}-dropdown` : undefined}>
        {PACKAGING_TYPE_GROUPS.map(({ group, options }, groupIndex) => (
          <Styled.Group key={group}>
            {groupIndex > 0 && <Styled.Divider />}
            <Styled.GroupLabel>{group}</Styled.GroupLabel>
            {options.map(option => (
              // Keyed by the option's value, because the three groups repeat
              // the same two labels — `Treated (T)` alone matches three rows.
              <Styled.CheckboxRow
                key={option.value}
                data-testid={testId ? `${testId}-${option.value}` : undefined}>
                <Checkbox
                  checked={selectedSet.has(option.value)}
                  disabled={disabled}
                  onChange={e => handleCheckboxChange(option.value, e)}>
                  {option.label}
                </Checkbox>
              </Styled.CheckboxRow>
            ))}
          </Styled.Group>
        ))}
      </Styled.DropdownContent>
    );

    return (
      <Styled.Popover
        content={content}
        trigger='click'
        open={disabled ? false : open}
        onOpenChange={setOpen}
        placement='bottomLeft'>
        <Styled.SelectTrigger
          ref={ref}
          data-testid={testId}
          $disabled={disabled}
          role='combobox'
          aria-expanded={!disabled && open}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={e => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              setOpen(prev => !prev);
            }
          }}>
          <Styled.SelectText $isPlaceholder={!displayText}>
            {displayText ?? 'Select'}
          </Styled.SelectText>
          <Styled.DownIcon />
        </Styled.SelectTrigger>
      </Styled.Popover>
    );
  }
);

PackagingTypeSelect.displayName = 'PackagingTypeSelect';

export default PackagingTypeSelect;
